import { Icon } from '#app/components/ui/icon'

/**
 * FooterBlock - Footer with company info, links, and social links
 *
 * This component renders a footer section with company information,
 * link columns, and optional social media links.
 * Design reference: TWBlocks Footer patterns
 *
 * Features:
 * - Company name and copyright
 * - Multi-column layout for links
 * - Social media icons
 * - Responsive design
 */

export interface LinkGroup {
	title?: string
	links?: Array<{ label: string; href: string }>
}

export interface SocialLink {
	platform: string
	href: string
	icon?: string
}

export interface FooterBlockProps {
	companyName?: string
	copyrightText?: string
	linkGroups?: LinkGroup[]
	socialLinks?: SocialLink[]
}

export function FooterBlock({
	companyName = 'Your Company',
	copyrightText = '© 2025 All rights reserved',
	linkGroups = [],
	socialLinks = [],
}: FooterBlockProps) {
	// Default link groups if none provided
	const displayLinkGroups =
		linkGroups.length > 0
			? linkGroups
			: [
					{
						title: 'Product',
						links: [
							{ label: 'Features', href: '/features' },
							{ label: 'Pricing', href: '/pricing' },
							{ label: 'FAQ', href: '/faq' },
						],
					},
					{
						title: 'Company',
						links: [
							{ label: 'About', href: '/about' },
							{ label: 'Blog', href: '/blog' },
							{ label: 'Contact', href: '/contact' },
						],
					},
			  ]

	// Default social links if none provided
	const displaySocialLinks =
		socialLinks.length > 0
			? socialLinks
			: [
					{ platform: 'Twitter', href: '#', icon: 'twitter' },
					{ platform: 'GitHub', href: '#', icon: 'github' },
					{ platform: 'LinkedIn', href: '#', icon: 'linkedin' },
			  ]

	return (
		<footer className="bg-muted w-full border-t py-12 md:py-16">
			<div className="container px-4 md:px-6">
				<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
					{/* Company Info */}
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<h3 className="text-lg font-semibold">{companyName}</h3>
							<p className="text-muted-foreground text-sm">{copyrightText}</p>
						</div>

						{/* Social Links */}
						{displaySocialLinks.length > 0 && (
							<div className="flex gap-4">
								{displaySocialLinks.map((social, index) => (
									<a
										key={index}
										href={social.href}
										className="text-muted-foreground hover:text-foreground transition-colors"
										aria-label={social.platform}
									>
										{social.icon && (
											<Icon name={social.icon as any} className="h-5 w-5" />
										)}
									</a>
								))}
							</div>
						)}
					</div>

					{/* Link Groups */}
					{displayLinkGroups.map((group, groupIndex) => (
						<div key={groupIndex} className="flex flex-col gap-4">
							{group.title && (
								<h4 className="text-sm font-semibold">{group.title}</h4>
							)}
							<ul className="flex flex-col gap-2">
								{group.links?.map((link, linkIndex) => (
									<li key={linkIndex}>
										<a
											href={link.href}
											className="text-muted-foreground hover:text-foreground text-sm transition-colors"
										>
											{link.label}
										</a>
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>
		</footer>
	)
}
